import { useEffect, useState } from "react";
import axios from "axios";
import { Tabs, Tab, Form, Row, Col, Button } from "react-bootstrap";

const AdminTaskManager = ({ userInfo }) => {
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState({});
    const [taskNotes, setTaskNotes] = useState({});
    const [loadingTaskId, setLoadingTaskId] = useState(null);
    const [message, setMessage] = useState("");
    const [newTask, setNewTask] = useState({ description: "", assignee: "", dueDate: "", frequency: "", note: "" });

    useEffect(() => {
        fetchTasks();
    }, []);

    const fetchTasks = async () => {
        const { data } = await axios.get("/api/tasks/admin");
        const myTasks = data.filter((t) => t.assignee?._id === userInfo._id);
        setTasks(myTasks);
        setFilteredTasks(categoriseTasks(myTasks));
    };

    const categoriseTasks = (taskList) => ({
        pending: taskList.filter((t) => t.status === "pending"),
        processing: taskList.filter((t) =>
            t.status === "processing" && (t.frequency === "one-time" || !t.frequency)
        ),
        completed: taskList.filter((t) => t.status === "completed"),
        declined: taskList.filter((t) => t.status === "declined"),
        ongoing: taskList.filter((t) =>
            (t.frequency === "weekly" || t.frequency === "fortnightly") && t.status === "processing"
        ),
        stopped: taskList.filter((t) => t.status === "stopped"),
    });

    const handleNoteChange = (taskId, note) => {
        setTaskNotes({ ...taskNotes, [taskId]: note });
    };

    const updateTaskStatus = async (taskId, status) => {
        setLoadingTaskId(taskId);
        const note = taskNotes[taskId] || "";
        await axios.put(`/api/tasks/admin/update/${taskId}`, { status, note });
        fetchTasks();
        if (status !== "completed") {
            setMessage("Task updated successfully");
            setTimeout(() => setMessage(""), 3000);
        }
    };

    const assignTask = async () => {
        const addDays = (date: Date, days: number): Date => {
            const copy = new Date(date);
            copy.setDate(copy.getDate() + days);
            return copy;
        };

        const taskToSubmit = {
            ...newTask,
            dueDate:
                newTask.frequency === "weekly"
                    ? addDays(new Date(), 7)
                    : newTask.frequency === "fortnightly"
                        ? addDays(new Date(), 14)
                        : newTask.dueDate,
            frequency: newTask.frequency || "one-time",
            assignee: userInfo?._id,
        };
        await axios.post("/api/tasks/admin/create", taskToSubmit);
        setNewTask({ description: "", assignee: "", dueDate: "", frequency: "", note: "" });
        fetchTasks();
    };

    return (
        <>
            {message && (
                <div
                    className="alert alert-success position-fixed"
                    style={{
                        top: "20%",
                        right: "5%",
                        zIndex: 2000,
                        minWidth: "200px",
                    }}
                >
                    {message}
                </div>
            )}
            <h5>Assign New Task</h5>
            <Form className="p-4 mb-4 bg-light rounded shadow-sm border">
                <Row className="mb-3 align-items-end">
                    <Col md={6} className="mb-2">
                        <Form.Label><strong>Task</strong></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Enter task description"
                            value={newTask.description}
                            onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                        />
                    </Col>
                    <Col md={3} className="mb-2">
                        <strong>Frequency</strong><br />
                        <div className="d-flex">
                            <Form.Check
                                label="One-time"
                                type="radio"
                                name="frequency"
                                onChange={() => setNewTask({ ...newTask, frequency: "one-time" })}
                                checked={newTask.frequency === "one-time" || !newTask.frequency}
                                className="me-3"
                            />
                            <Form.Check
                                label="Weekly"
                                type="radio"
                                name="frequency"
                                onChange={() => setNewTask({ ...newTask, frequency: "weekly" })}
                                checked={newTask.frequency === "weekly"}
                                className="me-3"
                            />
                            <Form.Check
                                label="Fortnightly"
                                type="radio"
                                name="frequency"
                                onChange={() => setNewTask({ ...newTask, frequency: "fortnightly" })}
                                checked={newTask.frequency === "fortnightly"}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className="align-items-center">
                    <Col md={6} className="mb-2">
                        <Form.Label><strong>Note</strong></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Additional instructions"
                            value={newTask.note}
                            onChange={(e) => setNewTask({ ...newTask, note: e.target.value })}
                        />
                    </Col>
                    <Col md={3} className="mb-2">
                        <Form.Label><strong>Due Date</strong></Form.Label>
                        <Form.Control
                            type="date"
                            value={newTask.dueDate || ""}
                            onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                            disabled={newTask.frequency === "weekly" || newTask.frequency === "fortnightly"}
                        />
                    </Col>

                    <Col md={2} className="text-start mt-4">
                        <Button variant="primary" onClick={assignTask}>Assign Task</Button>
                    </Col>
                </Row>
            </Form>
            <Tabs defaultActiveKey="pending" className="mt-3">
                {["pending", "processing", "ongoing", "completed", "declined", "stopped"].map((key) => (
                    <Tab eventKey={key} title={`${key[0].toUpperCase() + key.slice(1)} Tasks`} key={key}>
                        {filteredTasks[key]?.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Description</th>
                                            <th>Created Date</th>
                                            <th>Due Date</th>
                                            <th>Frequency</th>
                                            <th>Status</th>
                                            <th>Note</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTasks[key].map((task) => (
                                            <tr key={task._id}>
                                                <td style={{ width: "20%" }}>{task.description}</td>
                                                <td style={{ width: "10%" }}>{task.createdAt.slice(0, 10)}</td>
                                                <td style={{ width: "8%" }}>{task.dueDate ? task.dueDate.slice(0, 10) : "—"}</td>
                                                <td style={{ width: "8%" }}>{task.frequency || "One-time"}</td>
                                                <td style={{ width: "8%" }}>{task.status}</td>
                                                {task.status === "completed" || task.status === "declined" ? (
                                                    <td style={{ width: "30%" }}>{task.note}</td>
                                                ) : (
                                                    <td style={{ width: "30%" }}>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2} // adjust number of rows as needed
                                                            size="sm"
                                                            value={taskNotes[task._id] !== undefined ? taskNotes[task._id] : (task.note || "")}
                                                            onChange={(e) => handleNoteChange(task._id, e.target.value)}
                                                            className="mb-1"
                                                            style={{ resize: "vertical", whiteSpace: "pre-wrap" }} // optional: allow resizing and preserve line breaks
                                                        />
                                                    </td>
                                                )}

                                                <td style={{ width: "12%" }}>
                                                    {key === "pending" && (
                                                        <>

                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() => updateTaskStatus(task._id, "processing")}
                                                                className="me-1"
                                                            >
                                                                Accept
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => updateTaskStatus(task._id, "declined")}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </>
                                                    )}
                                                    {(key === "processing" || key === "ongoing") && (
                                                        <>
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => updateTaskStatus(task._id, task.status)}
                                                                className="me-1"
                                                            >
                                                                Update
                                                            </Button>
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => updateTaskStatus(task._id, "completed")}
                                                            >
                                                                Complete
                                                            </Button>
                                                        </>

                                                    )}
                                                    {(key === "completed" && task.frequency !== "one-time") && (
                                                        <>

                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => updateTaskStatus(task._id, "stopped")}
                                                            >
                                                                Stop
                                                            </Button>
                                                        </>

                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-3 text-center text-muted">No tasks found</div>
                        )}

                    </Tab>
                ))}
            </Tabs>
        </>

    );
};

export default AdminTaskManager;
